import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import api from '../api/api';
import type { SupplyPlan } from '../pages/management/OrderSupplyPage';
import './AiAssistant.css';

interface Status { available: boolean; message: string; requestsRemaining: number }
interface Advice { text: string; model: string; generatedAtUtc: string }

export const procurementAiErrorMessage = (error: unknown): string => {
  if (!axios.isAxiosError(error)) return 'Не вдалося отримати відповідь Gemini. Оновіть сторінку та перевірте підключення.';
  const data: unknown = error.response?.data;
  const message = data && typeof data === 'object' && 'message' in data && typeof data.message === 'string'
    ? data.message : null;
  const fallback: Record<number, string> = {
    400: 'Некоректний запит до ШІ. Оновіть сторінку.',
    403: 'Немає права користуватися ШІ-помічником.',
    429: 'Досягнуто ліміт запитів або Gemini ще обробляє попередній. Зачекайте й оновіть статус.',
    502: 'Шлюз або Gemini не зміг обробити запит. Перевірте модель і доступ у Google AI Studio.',
    503: 'ШІ-помічник недоступний. Перевірте його налаштування в адмінпанелі.',
    504: 'Час очікування Gemini вичерпано. Модель не встигла відповісти; спробуйте пізніше.',
    524: 'Шлюз припинив очікування відповіді. Оновіть статус перед повторним запитом.',
  };
  const text = message || (error.response ? fallback[error.response.status]
    || `Не вдалося отримати відповідь Gemini (HTTP ${error.response.status}).`
    : error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT'
      ? 'Час очікування відповіді сервера вичерпано. Оновіть статус перед повторним запитом.'
      : 'Немає зв’язку із сервером. Перевірте підключення та оновіть сторінку.');
  const correlation: unknown = error.response?.headers?.['x-correlation-id'];
  return typeof correlation === 'string' && /^[a-zA-Z0-9_.-]{1,100}$/.test(correlation)
    ? `${text} Код запиту: ${correlation}.` : text;
};

const topics = [
  { goal: 'priorities', label: 'Як визначити пріоритет закупівель?' },
  { goal: 'checklist', label: 'Що перевірити перед закупівлею?' },
  { goal: 'coverage', label: 'Як читати потребу та дефіцит?' },
];

const ProcurementAiAssistant: React.FC<{ plan: SupplyPlan }> = ({ plan }) => {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState<Status | null>(null);
  const [advice, setAdvice] = useState<Advice | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const request = useRef<AbortController | null>(null);
  const [refresh, setRefresh] = useState(0);

  useEffect(() => {
    if (!open) return;
    const controller = new AbortController();
    setStatus(null);
    api.get<Status>('/ai-assistant/status', { signal: controller.signal })
      .then(({ data }) => { if (!controller.signal.aborted) setStatus(data); })
      .catch(() => {
        if (!controller.signal.aborted) setError('Не вдалося перевірити доступність Gemini. Спробуйте оновити статус.');
      });
    return () => controller.abort();
  }, [open, refresh]);
  useEffect(() => () => request.current?.abort(), []);
  useEffect(() => {
    if (!loading) return;
    const started = Date.now();
    const timer = window.setInterval(() => setElapsed(Math.floor((Date.now() - started) / 1000)), 1000);
    return () => window.clearInterval(timer);
  }, [loading]);

  const ask = async (goal: string) => {
    if (!status?.available || loading) return;
    const controller = new AbortController();
    request.current = controller;
    setLoading(true); setElapsed(0); setError(''); setAdvice(null);
    try {
      // Business data stays in the browser; only a predefined topic reaches the API.
      const { data } = await api.post<Advice>('/ai-assistant/procurement-advice', { goal }, {
        signal: controller.signal, timeout: 90000,
      });
      if (!controller.signal.aborted) setAdvice(data);
    } catch (error: unknown) {
      if (!controller.signal.aborted) setError(procurementAiErrorMessage(error));
    } finally {
      if (!controller.signal.aborted) { setLoading(false); setRefresh(value => value + 1); }
    }
  };

  const priorities = plan.products.filter(product => product.missing > 0)
    .slice().sort((a, b) => (a.neededBy ?? '').localeCompare(b.neededBy ?? '') || a.productName.localeCompare(b.productName, 'uk'))
    .slice(0, 5);

  return <details className="ai-assistant" onToggle={event => setOpen(event.currentTarget.open)}>
    <summary>ШІ-помічник із закупівель · Gemini</summary>
    {open && <>
      <p>Gemini дає загальні рекомендації, не бачить даних CRM і не змінює документи.
        Точний дефіцит нижче — розрахунок CRM, не відповідь ШІ.</p>
      <h3>Найближчі потреби · дані CRM</h3>
      {priorities.length === 0 ? <p>У вибраному періоді дефіциту немає.</p> : <ul>
        {priorities.map(product => <li key={product.productId}>
          <strong>{product.productName}</strong>: {product.missing.toLocaleString('uk-UA', { maximumFractionDigits: 3 })} {product.unit}
          {product.neededBy && ` · до ${new Date(product.neededBy).toLocaleDateString('uk-UA')}`}
        </li>)}
      </ul>}
      <p>{status ? `${status.message} Залишилося запитів сьогодні: ${status.requestsRemaining}.` : 'Перевіряємо доступність Gemini…'}</p>
      <div className="ai-assistant-actions">
        {topics.map(topic => <button type="button" key={topic.goal} disabled={!status?.available || loading}
          onClick={() => ask(topic.goal)}>{topic.label}</button>)}
        <button type="button" disabled={loading} onClick={() => { setError(''); setRefresh(value => value + 1); }}>Оновити статус Gemini</button>
      </div>
      {loading && <p role="status">Gemini готує рекомендації… {elapsed} с.
        {elapsed >= 20 && ' Модель ще не відповіла. Очікуємо до 75 секунд; повторний запит поки не потрібен.'}</p>}
      {error && <p role="alert">{error}</p>}
      {advice && <section aria-label="Відповідь Gemini">
        <h3>Рекомендації ШІ · перевірте перед застосуванням</h3>
        <p className="ai-assistant-response">{advice.text}</p>
        <small>{advice.model} · {new Date(advice.generatedAtUtc).toLocaleString('uk-UA')}</small>
      </section>}
    </>}
  </details>;
};

export default ProcurementAiAssistant;
